import toast from "react-hot-toast";

/**
 * Stale-deploy protection.
 *
 * Two things go wrong when a new build is published while users have the app open:
 *
 *  1. The browser (or a CDN) keeps serving an old `index.html`, so the user keeps
 *     booting the previous build and never sees the fix that was deployed.
 *  2. The user has an old `index.html` that references chunk files which no longer
 *     exist on the server. Lazy routes then fail to import and the screen goes blank.
 *
 * `startVersionWatcher` handles (1) by comparing the build stamp compiled into this
 * bundle against `/version.json` on the server. `installStaleChunkRecovery` handles
 * (2) by reloading once when a dynamic import fails.
 */

/** Build stamp baked in at compile time; "dev" when running the Vite dev server. */
export const BUILD_ID = typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev";

/**
 * Prints the build stamp of the bundle this tab actually booted from.
 *
 * This is the reliable way to answer "is my fix live?": compare what this logs
 * against the `buildId` in the freshly deployed `dist/version.json`. If they
 * differ, the browser or CDN is still serving an older build and no amount of
 * re-testing the feature will tell you anything useful.
 */
export function logBuildBanner() {
  console.log(
    `%c Maleva %c build ${BUILD_ID} `,
    "background:#0f172a;color:#fff;border-radius:3px 0 0 3px;padding:2px 6px;font-weight:700",
    "background:#2563eb;color:#fff;border-radius:0 3px 3px 0;padding:2px 6px"
  );
}

const VERSION_URL = "/version.json";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_COUNT_KEY = "maleva:stale-chunk-reloads";
const MAX_RECOVERY_RELOADS = 2;

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();

    if (typeof data === "object" && data !== null && "buildId" in data) {
      const { buildId } = data as { buildId?: unknown };
      return typeof buildId === "string" ? buildId : null;
    }

    return null;
  } catch {
    // Offline or the file isn't deployed yet - never let this break the app.
    return null;
  }
}

export const APP_UPDATE_TOAST_ID = "app-update";
export const DISMISSED_BUILD_KEY = "maleva:dismissed-build-id";
export const RELOAD_PARAM_KEY = "_v";

/**
 * Removes temporary cache-busting query parameter added during an update reload.
 */
export function cleanReloadQueryParam() {
  if (typeof window !== "undefined" && window.location.search.includes(`${RELOAD_PARAM_KEY}=`)) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(RELOAD_PARAM_KEY);
      const cleanPath = url.pathname + (url.search ? url.search : "") + url.hash;
      window.history.replaceState({}, "", cleanPath);
    } catch {
      // ignore
    }
  }
}

/**
 * Dismisses the update toast and records the dismissed build in sessionStorage so
 * the current session is not repeatedly interrupted.
 */
export function dismissUpdatePrompt(deployedBuildId?: string) {
  toast.dismiss(APP_UPDATE_TOAST_ID);
  if (deployedBuildId && typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(DISMISSED_BUILD_KEY, deployedBuildId);
    } catch {
      // ignore
    }
  }
}

/**
 * Clears caches and forces a fresh reload bypassing HTTP disk cache.
 */
export async function reloadAppWithFreshBuild(navigateFn?: (url: string) => void) {
  toast.dismiss(APP_UPDATE_TOAST_ID);
  if (typeof window === "undefined") {
    return;
  }
  if ("caches" in window) {
    try {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
    } catch {
      // ignore
    }
  }
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM_KEY, Date.now().toString());
  if (navigateFn) {
    navigateFn(url.toString());
  } else if (typeof window.location?.replace === "function") {
    window.location.replace(url.toString());
  } else {
    window.location.href = url.toString();
  }
}

export function promptForUpdate(deployedBuildId: string) {
  toast.custom(
    (t) => (
      <div
        className={`flex items-center gap-3 rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-xl ring-1 ring-slate-800 transition-all ${
          t.visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>A new version of Maleva is available.</span>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <button
            type="button"
            onClick={() => void reloadAppWithFreshBuild()}
            className="rounded-md bg-white px-3 py-1 font-medium text-slate-900 transition-colors hover:bg-slate-200 cursor-pointer"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => dismissUpdatePrompt(deployedBuildId)}
            className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            aria-label="Dismiss update"
          >
            Dismiss
          </button>
        </div>
      </div>
    ),
    { id: APP_UPDATE_TOAST_ID, duration: Infinity, position: "bottom-center" }
  );
}

/**
 * Polls the deployed build stamp and offers a reload when the server is ahead of
 * this tab. No-op during development.
 */
export function startVersionWatcher() {
  if (import.meta.env.DEV) {
    return;
  }

  let alreadyPrompted = false;

  const check = async () => {
    if (alreadyPrompted || document.visibilityState !== "visible") {
      return;
    }

    const deployed = await fetchDeployedBuildId();

    if (deployed && deployed !== BUILD_ID) {
      const dismissed = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(DISMISSED_BUILD_KEY) : null;
      if (dismissed === deployed) {
        return;
      }
      alreadyPrompted = true;
      promptForUpdate(deployed);
    }
  };

  // Wait 30 seconds after initial boot before checking for an update to avoid
  // flashing an update prompt immediately while the user is loading a page.
  const bootTimer = window.setTimeout(() => void check(), 30_000);
  const intervalId = window.setInterval(() => void check(), CHECK_INTERVAL_MS);

  const handleVisibility = () => {
    if (document.visibilityState === "visible") {
      void check();
    }
  };
  const handleOnline = () => void check();

  window.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("online", handleOnline);

  return () => {
    window.clearTimeout(bootTimer);
    window.clearInterval(intervalId);
    window.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("online", handleOnline);
  };
}

function isStaleChunkError(message: unknown): boolean {
  return (
    typeof message === "string" &&
    /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|dynamically imported module/i.test(
      message
    )
  );
}

function recoverByReloading() {
  const attempts = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? "0");

  if (attempts >= MAX_RECOVERY_RELOADS) {
    // Reloading clearly isn't clearing it - stop looping and tell the user.
    toast.error(
      "This page is out of date and could not refresh itself. Please press Ctrl+F5.",
      { id: "stale-chunk", duration: Infinity }
    );
    return;
  }

  sessionStorage.setItem(RELOAD_COUNT_KEY, String(attempts + 1));
  window.location.reload();
}

/**
 * Reloads the page when a lazy chunk referenced by a stale `index.html` is missing
 * from the server, instead of leaving the user on a blank screen.
 */
export function installStaleChunkRecovery() {
  // Vite fires this when a preloaded/lazy module fails to load.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    recoverByReloading();
  });

  window.addEventListener("error", (event) => {
    if (isStaleChunkError(event.message)) {
      recoverByReloading();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    const message = reason instanceof Error ? reason.message : reason;

    if (isStaleChunkError(message)) {
      recoverByReloading();
    }
  });
}

/**
 * Clears the reload counter once the app has booted successfully, so a future
 * stale deploy still gets its recovery attempts.
 */
export function markBootSuccessful() {
  sessionStorage.removeItem(RELOAD_COUNT_KEY);
}
